/**
 * Current role IDs for the Commercial sales team.
 *
 * The UUID is the live pre-slug Sales Manager role. Keep it until #1117's
 * role-slug migration replaces all runtime ID matching.
 */
export const SALES_MANAGER_ROLE_IDS = [
  "sales_manager",
  "859306c9-a66c-4870-b4ab-8e3f2a2c14e7",
] as const;

/**
 * #2173: roles that may reach CROS purchasing (receipts, purchase_orders and,
 * from #2177, purchase_invoices) through query_ostendo_cros WITHOUT a resource
 * slice. Every prod role holding
 * that tool has a NULL allowed_ostendo_resources (fall-open), so the tool-enum
 * widening for freight would otherwise hand sales_rep supplier receipts and
 * open POs. A sliced role keeps slice semantics and never consults this set.
 * Ids: sales_manager + its live UUID, ceo_ops, admin, management, finance,
 * and the live Senior Management UUID.
 */
export const CROS_PURCHASING_UNSLICED_ROLE_IDS = [
  ...SALES_MANAGER_ROLE_IDS,
  "ceo_ops",
  "admin",
  "management",
  "finance",
  "c90fc456-9551-4286-ae70-193dd095786c",
] as const;

/**
 * The CROS purchasing resources the allow-set above gates. #2177 adds
 * purchase_invoices: PURCHASEINVOICES, the AP register, which is where ten of
 * the eleven freight carriers are invoiced with no purchase order at all.
 */
export const CROS_PURCHASING_RESOURCES = ["receipts", "purchase_orders", "purchase_invoices"] as const;

export const COMMERCIAL_SALES_ROLE_IDS = [
  ...SALES_MANAGER_ROLE_IDS,
  "sales_rep",
] as const;

const COMMERCIAL_SALES_ROLE_ID_SET: ReadonlySet<string> =
  new Set(COMMERCIAL_SALES_ROLE_IDS);

/** The role-specific landing route, or null when normal home behaviour applies. */
export function commercialSalesHome(roleIds: readonly string[]): string | null {
  return roleIds.some((id) => COMMERCIAL_SALES_ROLE_ID_SET.has(id))
    ? "/commercial/deliveries"
    : null;
}

/**
 * #1392 (Jez, Sun 13 Sep 2026, REPTOOL: strip now): the unscoped CROS Analyst
 * tools a `sales_rep` must never hold. `run_sql` and `query_ostendo_cros` are
 * arbitrary-query surfaces: neither can be row-scoped to one rep's customers,
 * so a rep holding either reads EVERY rep's commercial rows straight past the
 * #481 dashboard fence. The org model is commercial = Analyst OFF for reps.
 *
 * The live prod `roles` row still carries both (seeded by migrate-290 and
 * deliberately left alone by the 2026-08-07 polish), so the strip is enforced
 * HERE, in the resolver every gate reads, rather than only in D1: a stale or
 * re-seeded row cannot hand the tools back. `.jez/scripts/1392-strip-rep-tools.sql`
 * removes them from the row as well, so the two agree.
 *
 * Rep-scoped replacements are #1320's v2 design: a purpose-built,
 * server-filtered tool, never a widening of this pair.
 */
export const SALES_REP_DENIED_ANALYST_TOOLS = ["run_sql", "query_ostendo_cros"] as const;

/**
 * Roles the deny above applies to. Sales Manager keeps both (decision 4,
 * 2026-08-07).
 *
 * REPUNBLOCK, Akira 21 Sep 2026: **empty, so the strip applies to nobody.**
 * #1392 was written on 5 Aug on the premise "nothing urgent while no rep
 * accounts exist". George and Daniel were seeded as reps on 7 Aug and the strip
 * did not land until 13 Sep, by which point George had nine analyst sessions
 * behind him — stock by warehouse, product pricing, his own credit accounts.
 * Eight of the nine needed no cross-rep data at all. He has had no working
 * analyst since, and found out by being told his role "covers product
 * knowledge".
 *
 * The fence itself is now in question, not only its timing: Adam (head of
 * commercial) is not in favour of rep scoping and Albert has deferred to him,
 * so #481's row fence and #1320's rep-scoped replacement may both be withdrawn.
 * Akira's call is to restore reps first and refine afterwards.
 *
 * This list, not the constant above, is the lever: putting "sales_rep" back
 * reinstates #1392 in full with no other change.
 */
export const SALES_REP_ROLE_IDS: readonly string[] = [];

/**
 * #1392: the deny above, as a filter over an explicit tool list.
 *
 * Shared by the runtime resolver (server/lib/rbac.ts) and the Admin role
 * editor's read and save paths (server/routes/rbac.ts), so the editor shows and
 * persists what the runtime actually enforces. Without it the editor kept
 * displaying the two stripped tools as ticked and could write them straight
 * back, leaving the grant re-persistable forever.
 *
 * Explicit names only. A wildcard grant is handled by the resolver, which
 * narrows "*" to the catalogue minus this set.
 */
export function stripDeniedRepTools(
  roleId: string | null | undefined,
  tools: readonly string[],
): string[] {
  if (!roleId || !SALES_REP_ROLE_IDS.some((id) => id === roleId)) return [...tools];
  const denied: readonly string[] = SALES_REP_DENIED_ANALYST_TOOLS;
  return tools.filter((t) => !denied.includes(t));
}
